import { useMemo } from "react";
import FlexRow from "../../../../components/base/FlexRow";
import Icon from "../../../../components/base/Icon";
import { metroCovers } from "../../../../components/map/mapUtils";
import { HereGeocodePosition } from "../../../../models/MapModels";
import JobMetro from "../models/JobMetro";
import "./MetroOption.css";

export type MetroOptionItem = JobMetro & { coversJob?: boolean };

const NO_COLOR = "#a1a5b7";

/**
 * The metros for a Metro picker: those whose area holds the job's address come first, marked;
 * the rest keep their order.
 */
export function useMetroOptions(metros?: JobMetro[], jobPosition?: HereGeocodePosition): MetroOptionItem[] {
    return useMemo(() => {
        const marked = (metros ?? []).map(m => ({ ...m, coversJob: metroCovers(m, jobPosition) }));
        return [...marked.filter(m => m.coversJob), ...marked.filter(m => !m.coversJob)];
        // By the point, not the object: the same address re-set must not rebuild the picker.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    },[metros, jobPosition?.lat, jobPosition?.lng]);
}

/** A Metro picker option: its colour, its name, its technician count and state, and whether it covers the job. */
export function renderMetroOption(metro: MetroOptionItem) {
    const color = metro.color || NO_COLOR;
    const count = metro.techIds?.length ?? 0;
    return (
        <FlexRow className="w-100 py-1">
            <span className="metro-option-swatch" style={{ backgroundColor: color, boxShadow: `0 0 0 4px color-mix(in srgb, ${color} 22%, transparent)` }} />
            <div className="d-flex flex-column ms-4 flex-grow-1 min-w-0">
                <span className="fw-bold text-gray-800 text-truncate">{metro.name}</span>
                <span className="fs-8 fw-normal text-gray-500 text-truncate">
                    <Icon icon="user-helmet-safety" size={10} className="me-1" />
                    {count === 1 ? "1 technician" : `${count} technicians`}
                    {metro.stateName && <> &middot; {metro.stateName}</>}
                </span>
            </div>
            {
                metro.coversJob &&
                <span className="badge badge-light-success fs-9 ms-2 flex-shrink-0" title="The job's address is inside this metro">
                    <Icon icon="location-dot" type="solid" size={9} className="me-1" />
                    Covers job
                </span>
            }
        </FlexRow>
    );
}

/** The picked metro in the closed picker: its colour and name. */
export function renderMetroSelection(metro: JobMetro) {
    return (
        <span className="d-flex align-items-center">
            <span className="metro-option-swatch small" style={{ backgroundColor: metro.color || NO_COLOR }} />
            <span className="ms-3 text-truncate">{metro.name}</span>
        </span>
    );
}
